import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, type Domain } from '../api';
import { DownloadIcon, GlobeIcon, PlusIcon, SearchIcon } from '../components/icons';
import {
  CodeButton,
  Dialog,
  downloadCsv,
  Empty,
  ErrorNote,
  PageHeader,
  RowMenu,
  SkeletonRows,
  Status,
  timeAgo,
  toneOf,
  useToast,
} from '../components/ui';
import { useLoad } from '../hooks';
import { useSession } from '../session';

export function Domains() {
  const navigate = useNavigate();
  const { team } = useSession();
  const canEdit = team.role !== 'member';
  const { data, error, loading, reload } = useLoad(
    () => api<{ data: Domain[] }>('GET', '/domains'),
    [],
  );
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [deleting, setDeleting] = useState<Domain | null>(null);
  const toast = useToast();
  const all = data?.data ?? [];
  const domains = all.filter(
    (d) => (!status || d.status === status) && d.name.includes(search.trim().toLowerCase()),
  );

  async function check(domain: Domain) {
    try {
      const updated = await api<Domain>('POST', `/domains/${encodeURIComponent(domain.id)}/verify`);
      toast.show(
        updated.status === 'verified'
          ? `${domain.name} is verified`
          : 'Some records are still missing',
      );
      reload();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Check failed');
    }
  }

  return (
    <>
      <PageHeader
        title="Domains"
        description="The domains you send from, and the DNS records that verify them."
        actions={
          <>
            {canEdit ? (
              <Link to="/domains/new" className="btn btn-primary">
                <PlusIcon />
                Add domain
              </Link>
            ) : null}
            <CodeButton
              title="Manage domains"
              snippets={[
                {
                  label: 'cURL',
                  code: `curl -X POST https://api.vaartix.com/domains \\
  -H "Authorization: Bearer $VAARTIX_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "mail.acme.in" }'`,
                },
              ]}
            />
          </>
        }
      />
      <ErrorNote error={error} />

      {!loading && all.length === 0 ? (
        <Empty
          icon={<GlobeIcon />}
          title="Add your first domain"
          actions={
            canEdit ? (
              <Link to="/domains/new" className="btn btn-primary">
                <PlusIcon />
                Add domain
              </Link>
            ) : null
          }
        >
          <p>
            Send from your own address, like <code>orders@yourapp.in</code>. Add a few DNS records
            and most domains verify within an hour.
          </p>
        </Empty>
      ) : (
        <>
          <div className="filters">
            <div className="search">
              <SearchIcon />
              <input
                className="input"
                type="search"
                placeholder="Search domains…"
                aria-label="Search domains"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <select
              className="input"
              aria-label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="temporary_failure">Retrying</option>
              <option value="failed">Failed</option>
            </select>
            <button
              type="button"
              className="btn btn-ghost btn-icon hide-sm"
              aria-label="Export as CSV"
              title="Export as CSV"
              disabled={domains.length === 0}
              onClick={() =>
                downloadCsv(
                  'vaartix-domains.csv',
                  domains.map((d) => ({
                    id: d.id,
                    name: d.name,
                    status: d.status,
                    region: d.region,
                    createdAt: d.createdAt,
                  })),
                )
              }
            >
              <DownloadIcon />
            </button>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Status</th>
                  <th className="hide-sm">Region</th>
                  <th className="nowrap hide-sm">Created</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows cols={5} rows={3} />
                ) : (
                  domains.map((domain) => (
                    <tr
                      key={domain.id}
                      className="row-link"
                      tabIndex={0}
                      onClick={() => navigate(`/domains/${domain.id}`)}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/domains/${domain.id}`)}
                    >
                      <td>
                        <div className="cell-main">
                          <span className={`row-icon ${toneOf(domain.status)}`}>
                            <GlobeIcon />
                          </span>
                          <strong>{domain.name}</strong>
                        </div>
                      </td>
                      <td>
                        <Status status={domain.status} />
                      </td>
                      <td className="hide-sm muted">Mumbai ({domain.region})</td>
                      <td className="nowrap hide-sm muted">{timeAgo(domain.createdAt)}</td>
                      <td className="right">
                        <RowMenu
                          items={[
                            {
                              label: 'View DNS records',
                              onSelect: () => navigate(`/domains/${domain.id}`),
                            },
                            ...(canEdit && domain.status !== 'verified'
                              ? [{ label: 'Check now', onSelect: () => void check(domain) }]
                              : []),
                            ...(canEdit
                              ? [
                                  {
                                    label: 'Delete',
                                    danger: true,
                                    onSelect: () => setDeleting(domain),
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="table-foot">
            <span>
              {domains.length} of {all.length} domain{all.length === 1 ? '' : 's'}
            </span>
          </div>
        </>
      )}

      <p className="page-note">
        Once a domain is verified you can send from any address on it, such as <code>orders@</code>{' '}
        or <code>login@</code>. You choose the sender in each email's <code>from</code> when you
        call the API; nothing is set up per address.
      </p>

      {deleting ? (
        <Dialog
          title={`Delete ${deleting.name}?`}
          description="Emails can no longer be sent from this domain. You can add it again later, but you'll need to verify it again."
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setDeleting(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  const domain = deleting;
                  setDeleting(null);
                  try {
                    await api('DELETE', `/domains/${encodeURIComponent(domain.id)}`);
                    toast.show(`${domain.name} deleted`);
                    reload();
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not delete');
                  }
                }}
              >
                Delete domain
              </button>
            </>
          }
        />
      ) : null}
      {toast.node}
    </>
  );
}
